import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { 
  Users, DollarSign, FileText, Activity, LogOut, Search,
  ChevronRight, Loader2, Eye, Trash2, CreditCard, ClipboardList,
  CheckCircle, XCircle, AlertCircle, User, Mail, Phone, Calendar, UserPlus
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import SEO from "../components/SEO";
import ClientProfile from "./ClientProfile";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Login Component
const AdminLogin = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const response = await axios.post(`${API}/admin/login`, { email, password });
      localStorage.setItem("adminToken", response.data.token);
      onLogin(response.data.token);
      toast.success("Welcome back!");
    } catch (error) {
      toast.error("Invalid credentials");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 aurora-bg">
      <div className="orb-cyan top-20 left-20" />
      <div className="orb-purple bottom-20 right-20" />
      
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-md"
      >
        <Card className="glass-card border-white/10">
          <CardHeader className="text-center">
            <CardTitle className="font-italiana text-3xl text-white">Admin Login</CardTitle>
            <p className="text-white/60 text-sm mt-2">Glamour Body CRM</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-white/70 text-sm mb-2">Email</label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-dark rounded-lg"
                  placeholder="admin@email.com"
                  required
                />
              </div>
              <div>
                <label className="block text-white/70 text-sm mb-2">Password</label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-dark rounded-lg"
                  placeholder="••••••••"
                  required
                />
              </div>
              <Button 
                type="submit" 
                disabled={isLoading}
                className="w-full btn-primary rounded-full py-4"
              >
                {isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Sign In"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

// Stats Card Component
const StatsCard = ({ title, value, icon: Icon, color }) => (
  <Card className="glass-card border-white/10">
    <CardContent className="p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-white/60 text-sm">{title}</p>
          <p className={`text-3xl font-syne mt-1 ${color}`}>{value}</p>
        </div>
        <div className={`p-3 rounded-full bg-gradient-to-r ${color === 'text-cyan-400' ? 'from-cyan-500/20 to-cyan-500/10' : color === 'text-emerald-400' ? 'from-emerald-500/20 to-emerald-500/10' : color === 'text-purple-400' ? 'from-purple-500/20 to-purple-500/10' : 'from-yellow-500/20 to-yellow-500/10'}`}>
          <Icon className={color} size={24} />
        </div>
      </div>
    </CardContent>
  </Card>
);

// Member Row Component
const MemberRow = ({ member, onView, onCharge, onDelete }) => (
  <motion.tr 
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    className="border-b border-white/5 hover:bg-white/5"
  >
    <td className="py-4 px-4">
      <div>
        <p className="text-white font-medium">{member.name}</p>
        <p className="text-white/50 text-sm">{member.email}</p>
      </div>
    </td>
    <td className="py-4 px-4 text-white/70">{member.phone}</td>
    <td className="py-4 px-4">
      <span className={`px-2 py-1 rounded-full text-xs ${
        member.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' :
        member.status === 'lead' ? 'bg-yellow-500/20 text-yellow-400' :
        'bg-gray-500/20 text-gray-400'
      }`}>
        {member.status}
      </span>
    </td>
    <td className="py-4 px-4 text-cyan-400 font-syne">${member.total_spent?.toFixed(2) || '0.00'}</td>
    <td className="py-4 px-4">
      {member.health_form_completed ? 
        <CheckCircle className="text-emerald-400" size={18} /> : 
        <XCircle className="text-red-400" size={18} />
      }
    </td>
    <td className="py-4 px-4">
      <div className="flex items-center gap-2">
        <Button size="sm" variant="ghost" onClick={() => onView(member)} className="text-white/60 hover:text-white">
          <Eye size={16} />
        </Button>
        <Button size="sm" variant="ghost" onClick={() => onCharge(member)} className="text-cyan-400 hover:text-cyan-300">
          <CreditCard size={16} />
        </Button>
        <Button size="sm" variant="ghost" onClick={() => onDelete(member)} className="text-red-400 hover:text-red-300">
          <Trash2 size={16} />
        </Button>
      </div>
    </td>
  </motion.tr>
);

// Member Detail Modal
const MemberDetailModal = ({ member, isOpen, onClose, token }) => {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen && member) {
      fetchMemberDetails();
    }
  }, [isOpen, member]);

  const fetchMemberDetails = async () => {
    try {
      const response = await axios.get(`${API}/admin/members/${member.id}?token=${token}`);
      setDetails(response.data);
    } catch (error) {
      toast.error("Failed to load member details");
    } finally {
      setLoading(false);
    }
  };

  if (!member) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="glass-card border-white/20 max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-italiana text-2xl text-white">{member.name}</DialogTitle>
          <DialogDescription className="text-white/60">Member Details & History</DialogDescription>
        </DialogHeader>
        
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 text-cyan-400 animate-spin" />
          </div>
        ) : details ? (
          <div className="space-y-6">
            {/* Contact Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-2 text-white/70">
                <Mail size={16} className="text-cyan-400" />
                {details.member.email}
              </div>
              <div className="flex items-center gap-2 text-white/70">
                <Phone size={16} className="text-cyan-400" />
                {details.member.phone}
              </div>
            </div>
            
            {/* Stats */}
            <div className="grid grid-cols-3 gap-4">
              <div className="glass-card rounded-lg p-4 text-center">
                <p className="text-white/60 text-xs">Total Spent</p>
                <p className="text-cyan-400 font-syne text-xl">${details.member.total_spent?.toFixed(2) || '0.00'}</p>
              </div>
              <div className="glass-card rounded-lg p-4 text-center">
                <p className="text-white/60 text-xs">Services</p>
                <p className="text-emerald-400 font-syne text-xl">{details.member.services_purchased?.length || 0}</p>
              </div>
              <div className="glass-card rounded-lg p-4 text-center">
                <p className="text-white/60 text-xs">Status</p>
                <p className={`font-syne text-xl ${details.member.status === 'active' ? 'text-emerald-400' : 'text-yellow-400'}`}>
                  {details.member.status}
                </p>
              </div>
            </div>
            
            {/* Health Form Status */}
            <div className="glass-card rounded-lg p-4">
              <div className="flex items-center justify-between">
                <span className="text-white/70">Health Form</span>
                {details.health_form ? (
                  <span className="flex items-center gap-2 text-emerald-400">
                    <CheckCircle size={16} /> Completed
                  </span>
                ) : (
                  <span className="flex items-center gap-2 text-yellow-400">
                    <AlertCircle size={16} /> Pending
                  </span>
                )}
              </div>
            </div>
            
            {/* Transaction History */}
            <div>
              <h4 className="text-white font-semibold mb-3">Transaction History</h4>
              {details.transactions?.length > 0 ? (
                <div className="space-y-2">
                  {details.transactions.map((tx) => (
                    <div key={tx.id} className="glass-card rounded-lg p-3 flex items-center justify-between">
                      <div>
                        <p className="text-white text-sm">{tx.service_name}</p>
                        <p className="text-white/50 text-xs">{new Date(tx.created_at).toLocaleDateString()}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-cyan-400 font-syne">${tx.amount}</p>
                        <span className={`text-xs ${tx.payment_status === 'paid' ? 'text-emerald-400' : 'text-yellow-400'}`}>
                          {tx.payment_status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-white/50 text-sm">No transactions yet</p>
              )}
            </div>
            
            {/* Notes */}
            {details.member.notes && (
              <div>
                <h4 className="text-white font-semibold mb-2">Notes</h4>
                <p className="text-white/70 text-sm">{details.member.notes}</p>
              </div>
            )}
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

// Add Client Modal
const AddClientModal = ({ isOpen, onClose, token, onSuccess }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [serviceInterest, setServiceInterest] = useState("");
  const [notes, setNotes] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !email || !phone) {
      toast.error("Please fill in name, email, and phone");
      return;
    }
    
    setIsLoading(true);
    try {
      await axios.post(`${API}/admin/members?token=${token}`, {
        name,
        email,
        phone,
        service_interest: serviceInterest || null,
        message: notes || null
      });
      toast.success("Client added successfully!");
      setName(""); setEmail(""); setPhone(""); setServiceInterest(""); setNotes("");
      onSuccess();
      onClose();
    } catch (error) {
      const msg = error.response?.data?.detail || "Failed to add client";
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="glass-card border-white/20 max-w-md">
        <DialogHeader>
          <DialogTitle className="font-italiana text-2xl text-white">Add New Client</DialogTitle>
          <DialogDescription className="text-white/60">
            Create a new client profile in the CRM
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-white/70 text-sm mb-1">Name *</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="input-dark rounded-lg" placeholder="Full name" required data-testid="add-client-name" />
          </div>
          <div>
            <label className="block text-white/70 text-sm mb-1">Email *</label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="input-dark rounded-lg" placeholder="email@example.com" required data-testid="add-client-email" />
          </div>
          <div>
            <label className="block text-white/70 text-sm mb-1">Phone *</label>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} className="input-dark rounded-lg" placeholder="(555) 123-4567" required data-testid="add-client-phone" />
          </div>
          <div>
            <label className="block text-white/70 text-sm mb-1">Service Interest</label>
            <Input value={serviceInterest} onChange={(e) => setServiceInterest(e.target.value)} className="input-dark rounded-lg" placeholder="e.g., Signature Glam Facial" data-testid="add-client-service" />
          </div>
          <div>
            <label className="block text-white/70 text-sm mb-1">Notes</label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="input-dark rounded-lg" placeholder="Any notes about this client..." rows={3} data-testid="add-client-notes" />
          </div>
          
          <Button type="submit" disabled={isLoading} className="w-full btn-primary rounded-full" data-testid="add-client-submit">
            {isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Add Client"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Charge Modal
const ChargeModal = ({ member, isOpen, onClose, token, services }) => {
  const [selectedService, setSelectedService] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleCharge = async () => {
    if (!selectedService) {
      toast.error("Please select a service");
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/admin/charge?token=${token}`, {
        member_id: member.id,
        service_id: selectedService,
        origin_url: window.location.origin
      });
      
      if (response.data.url) {
        // Open Stripe checkout in new tab
        window.open(response.data.url, '_blank');
        toast.success("Checkout link opened in new tab");
        onClose();
      }
    } catch (error) {
      toast.error("Failed to create charge");
    } finally {
      setIsLoading(false);
    }
  };

  if (!member) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="glass-card border-white/20 max-w-md">
        <DialogHeader>
          <DialogTitle className="font-italiana text-2xl text-white">Charge Member</DialogTitle>
          <DialogDescription className="text-white/60">
            Create a payment link for {member.name}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          <div>
            <label className="block text-white/70 text-sm mb-2">Select Service</label>
            <Select value={selectedService} onValueChange={setSelectedService}>
              <SelectTrigger className="input-dark rounded-lg">
                <SelectValue placeholder="Choose a service" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-white/20 max-h-60">
                {services.map((service) => (
                  <SelectItem key={service.id} value={service.id} className="text-white hover:bg-white/10">
                    {service.name} - ${service.price}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <Button 
            onClick={handleCharge}
            disabled={isLoading}
            className="w-full btn-primary rounded-full"
          >
            {isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Generate Payment Link"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Main Admin Dashboard
const AdminDashboard = () => {
  const navigate = useNavigate();
  const [token, setToken] = useState(localStorage.getItem("adminToken") || "");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [members, setMembers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedMember, setSelectedMember] = useState(null);
  const [showChargeModal, setShowChargeModal] = useState(false);
  const [showAddClientModal, setShowAddClientModal] = useState(false);
  const [viewingMemberId, setViewingMemberId] = useState(null);
  const [activeTab, setActiveTab] = useState("members");
  const [services, setServices] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [healthForms, setHealthForms] = useState([]);

  useEffect(() => {
    if (token) {
      verifyToken();
    } else {
      setIsLoading(false);
    }
  }, [token]);

  const verifyToken = async () => {
    try {
      await axios.get(`${API}/admin/verify?token=${token}`);
      setIsAuthenticated(true);
      fetchData();
    } catch (error) {
      localStorage.removeItem("adminToken");
      setToken("");
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      const [statsRes, membersRes, servicesRes, transactionsRes, healthFormsRes] = await Promise.all([
        axios.get(`${API}/admin/stats?token=${token}`),
        axios.get(`${API}/admin/members?token=${token}`),
        axios.get(`${API}/services`),
        axios.get(`${API}/admin/transactions?token=${token}`),
        axios.get(`${API}/admin/health-forms?token=${token}`)
      ]);
      
      setStats(statsRes.data);
      setMembers(membersRes.data);
      setTransactions(transactionsRes.data);
      setHealthForms(healthFormsRes.data);
      
      // Flatten services
      const flatServices = [];
      Object.values(servicesRes.data).forEach((category) => {
        category.items.forEach((item) => flatServices.push(item));
      });
      setServices(flatServices);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    setToken("");
    setIsAuthenticated(false);
  };

  const handleDeleteMember = async (member) => {
    if (!window.confirm(`Are you sure you want to delete ${member.name}?`)) return;
    
    try {
      await axios.delete(`${API}/admin/members/${member.id}?token=${token}`);
      toast.success("Member deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete member");
    }
  };

  const filteredMembers = members.filter(m => 
    m.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    m.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    m.phone?.includes(searchTerm)
  );

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center aurora-bg">
        <Loader2 className="h-12 w-12 text-cyan-400 animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <AdminLogin onLogin={(t) => { setToken(t); setIsAuthenticated(true); fetchData(); }} />;
  }

  return (
    <>
      <SEO title="Admin Dashboard" description="Glamour Body CRM Admin" />
      
      <div className="min-h-screen bg-[#050505]">
        {/* Header */}
        <header className="glass-card border-b border-white/10 sticky top-0 z-40">
          <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
            <h1 className="font-italiana text-2xl text-white">Glamour Body CRM</h1>
            <Button onClick={handleLogout} variant="ghost" className="text-white/60 hover:text-white">
              <LogOut size={18} className="mr-2" /> Logout
            </Button>
          </div>
        </header>
        
        <main className="max-w-7xl mx-auto px-4 py-8">
          {viewingMemberId ? (
            <ClientProfile
              memberId={viewingMemberId}
              token={token}
              onBack={() => { setViewingMemberId(null); fetchData(); }}
            />
          ) : (
          <>
          {/* Stats Grid */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <StatsCard title="Total Members" value={stats.total_members} icon={Users} color="text-cyan-400" />
              <StatsCard title="Active Members" value={stats.active_members} icon={Activity} color="text-emerald-400" />
              <StatsCard title="Total Revenue" value={`$${stats.total_revenue?.toFixed(2)}`} icon={DollarSign} color="text-purple-400" />
              <StatsCard title="Pending Forms" value={stats.pending_health_forms} icon={FileText} color="text-yellow-400" />
            </div>
          )}
          
          {/* Tabs */}
          <div className="flex gap-4 mb-6">
            {["members", "transactions", "health-forms"].map((tab) => (
              <Button
                key={tab}
                onClick={() => setActiveTab(tab)}
                variant={activeTab === tab ? "default" : "ghost"}
                className={activeTab === tab ? "btn-primary rounded-full" : "text-white/60 hover:text-white"}
              >
                {tab === "members" && <Users size={16} className="mr-2" />}
                {tab === "transactions" && <CreditCard size={16} className="mr-2" />}
                {tab === "health-forms" && <ClipboardList size={16} className="mr-2" />}
                {tab.charAt(0).toUpperCase() + tab.slice(1).replace("-", " ")}
              </Button>
            ))}
          </div>
          
          {/* Members Tab */}
          {activeTab === "members" && (
            <Card className="glass-card border-white/10">
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <CardTitle className="text-white">Members</CardTitle>
                  <div className="flex items-center gap-3">
                    <div className="relative w-64">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" size={16} />
                      <Input
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search members..."
                        className="input-dark rounded-full pl-10"
                        data-testid="search-members-input"
                      />
                    </div>
                    <Button 
                      onClick={() => setShowAddClientModal(true)} 
                      className="btn-primary rounded-full"
                      data-testid="add-client-btn"
                    >
                      <UserPlus size={16} className="mr-2" /> Add Client
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="text-left py-3 px-4 text-white/60 text-sm font-medium">Name</th>
                        <th className="text-left py-3 px-4 text-white/60 text-sm font-medium">Phone</th>
                        <th className="text-left py-3 px-4 text-white/60 text-sm font-medium">Status</th>
                        <th className="text-left py-3 px-4 text-white/60 text-sm font-medium">Spent</th>
                        <th className="text-left py-3 px-4 text-white/60 text-sm font-medium">Health Form</th>
                        <th className="text-left py-3 px-4 text-white/60 text-sm font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredMembers.map((member) => (
                        <MemberRow
                          key={member.id}
                          member={member}
                          onView={(m) => { setViewingMemberId(m.id); }}
                          onCharge={(m) => { setSelectedMember(m); setShowChargeModal(true); }}
                          onDelete={handleDeleteMember}
                        />
                      ))}
                    </tbody>
                  </table>
                  {filteredMembers.length === 0 && (
                    <p className="text-center text-white/50 py-8">No members found</p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* Transactions Tab */}
          {activeTab === "transactions" && (
            <Card className="glass-card border-white/10">
              <CardHeader>
                <CardTitle className="text-white">Transactions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {transactions.map((tx) => (
                    <div key={tx.id} className="glass-card rounded-lg p-4 flex items-center justify-between">
                      <div>
                        <p className="text-white font-medium">{tx.service_name}</p>
                        <p className="text-white/50 text-sm">
                          {new Date(tx.created_at).toLocaleDateString()} • {tx.member_id ? 'Member' : 'Guest'}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-cyan-400 font-syne text-lg">${tx.amount}</p>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          tx.payment_status === 'paid' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-yellow-500/20 text-yellow-400'
                        }`}>
                          {tx.payment_status}
                        </span>
                      </div>
                    </div>
                  ))}
                  {transactions.length === 0 && (
                    <p className="text-center text-white/50 py-8">No transactions yet</p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* Health Forms Tab */}
          {activeTab === "health-forms" && (
            <Card className="glass-card border-white/10">
              <CardHeader>
                <CardTitle className="text-white">Health Forms</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {healthForms.map((form) => (
                    <div key={form.id} className="glass-card rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-white font-medium">Member: {form.member_id}</p>
                        <p className="text-white/50 text-sm">{new Date(form.created_at).toLocaleDateString()}</p>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-white/50">Skin Type</p>
                          <p className="text-white">{form.skin_type || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-white/50">Conditions</p>
                          <p className="text-white">{form.health_conditions?.length || 0} reported</p>
                        </div>
                        <div>
                          <p className="text-white/50">Allergies</p>
                          <p className="text-white">{form.allergies || 'None'}</p>
                        </div>
                        <div>
                          <p className="text-white/50">Signature</p>
                          <p className="text-emerald-400">{form.consent_signature}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                  {healthForms.length === 0 && (
                    <p className="text-center text-white/50 py-8">No health forms submitted</p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
          </>
          )}
        </main>
        
        {/* Modals */}
        <ChargeModal
          member={selectedMember}
          isOpen={showChargeModal}
          onClose={() => setShowChargeModal(false)}
          token={token}
          services={services}
        />
        
        <AddClientModal
          isOpen={showAddClientModal}
          onClose={() => setShowAddClientModal(false)}
          token={token}
          onSuccess={fetchData}
        />
      </div>
    </>
  );
};

export default AdminDashboard;
